/**
 * Welcome Kit — free gift for subscriptions
 *
 * Keeps the kit line's quantity equal to the number of subscription LINES in
 * the cart. Two subscriptions means qty 2; one subscription bought x3 is still
 * qty 1, because it is one line.
 *
 * Correctness comes from the Shopify cart API, never from the DOM.
 *
 * The kit line is hidden in the cart drawer (see welcome-kit-data.liquid) and
 * appears only at checkout, so none of this is ever on screen.
 *
 * Two things belonging to the cart app do the heavy lifting. Both were read out
 * of its own bundle (cart_renderer_v2.js). Neither is documented, so each is
 * treated as optional and allowed to be missing:
 *
 *   'cart:updated'     dispatched on document with the new item list on every
 *                      cart change — including the optimistic one fired the
 *                      moment a customer clicks the bin. Adds, removals and
 *                      quantity changes all arrive here, so this is the only
 *                      thing that drives the kit.
 *   mu_checkoutHook()  awaited before it sends the customer to checkout. The
 *                      last moment being right still matters, and the reason
 *                      nothing else has to be fast.
 *
 * Loaded above {{ content_for_header }} so it is in place before app scripts.
 * Settings come from window.BescherWelcomeKit (snippets/welcome-kit-data.liquid).
 *
 * Debug: localStorage.setItem('welcome_kit_debug', '1')
 */
(function () {
  'use strict';

  /* Not _free_gift: the cart app uses that name for its own gifts, and claims
     any product whose Shopify product type is "free_gift". Ours avoids both. */
  var MARKER_KEY = '_welcome_kit';
  var MARKER_VALUE = 'bescher';

  /* Let a burst of changes settle before writing. One removal makes the app
     send a change and then an update, and that update resends its own view of
     every line — which re-creates a line removed in between. */
  var QUIET_MS = 250;
  /* One retry covers losing a race for the cart lock (HTTP 422). */
  var RETRY_MS = 1200;
  /* Never hold a customer at checkout longer than this, whatever is going on. */
  var CHECKOUT_TIMEOUT_MS = 2500;
  /* Stop rather than join a runaway involving another app. */
  var MAX_WRITES = 6;
  var WRITE_WINDOW_MS = 10000;
  /* How long an item list handed to us by the app stays trustworthy. */
  var SNAPSHOT_TTL_MS = 2000;

  /* Captured before any app can wrap it. */
  var nativeFetch = window.fetch ? window.fetch.bind(window) : null;

  var startedAt = Date.now();
  var timer = null;
  var running = false;
  var queued = false;
  var stopped = false;
  var retried = false;
  var writeLog = [];
  var snapshot = null;
  var snapshotAt = 0;

  function settings() {
    return window.BescherWelcomeKit || {};
  }

  function debug() {
    var on = settings().debug;

    if (!on) {
      try {
        on = window.localStorage.getItem('welcome_kit_debug') === '1';
      } catch (e) {
        return;
      }
    }

    if (!on) return;

    try {
      var at = '+' + ((Date.now() - startedAt) / 1000).toFixed(2) + 's';
      console.log.apply(console, ['[welcome-kit ' + at + ']'].concat(Array.prototype.slice.call(arguments)));
    } catch (e) {
      /* logging must never break the cart */
    }
  }

  /* An array even though one product is used today: the logic loops. */
  function kitProducts() {
    var config = settings();

    if (!config.enabled || !Array.isArray(config.cartItems)) return [];

    return config.cartItems.filter(function (item) {
      return item && item.variantId;
    });
  }

  function overBudget() {
    var now = Date.now();

    writeLog = writeLog.filter(function (at) {
      return now - at < WRITE_WINDOW_MS;
    });

    if (writeLog.length < MAX_WRITES) return false;

    stopped = true;

    try {
      console.warn('[welcome-kit] too many cart writes in a row — stopping. Reload to resume.');
    } catch (e) {
      /* no-op */
    }

    return true;
  }

  /* --- the cart ----------------------------------------------------------- */

  function getCart() {
    var root = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/';

    return nativeFetch(root + 'cart.js', {
      method: 'GET',
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    }).then(function (response) {
      return response.json();
    });
  }

  /* Shopify serializes cart access, so a read issued while the app is writing
     queues behind it — measured at two seconds on this store. The app hands us
     its item list with every 'cart:updated', so most of the time we already
     have what a read would have told us. */
  function readCart() {
    if (snapshot && Date.now() - snapshotAt < SNAPSHOT_TTL_MS) {
      var fresh = snapshot;
      snapshot = null;
      debug('using the item list the app just published, no read needed');
      return Promise.resolve(fresh);
    }

    return getCart();
  }

  function post(path, body) {
    debug('POST', path, body);

    return nativeFetch(path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body),
    }).then(function (response) {
      if (response.ok) return response.json();

      return response.text().then(function (text) {
        debug('POST ' + path + ' rejected — HTTP ' + response.status, text);
        var error = new Error('Welcome Kit: ' + path + ' failed (' + response.status + ')');
        error.status = response.status;
        throw error;
      });
    });
  }

  /* Sequential: the cart API does not take concurrent writes. */
  function apply(operations) {
    return operations.reduce(function (chain, operation) {
      return chain.then(function () {
        if (operation.add) return post('/cart/add.js', { items: [operation.add] });
        return post('/cart/change.js', { id: operation.id, quantity: operation.quantity });
      });
    }, Promise.resolve());
  }

  /* monster_refresh(open, done) re-reads the cart and pushes it into the app's
     own state. The first argument decides whether it also OPENS the drawer and
     defaults to true, which is why a redraw arriving after the customer closed
     the drawer used to pop it back up. False refreshes silently.

     Not optional: the app holds the cart in memory and does not re-read it when
     the drawer opens, so skipping this leaves the drawer showing a stale cart —
     and a stale cart is what re-creates a line we just removed. */
  function refreshDrawer() {
    try {
      if (typeof window.monster_refresh === 'function') window.monster_refresh(false);
    } catch (e) {
      /* never let a drawer redraw break cart state */
    }
  }

  /* --- what the cart should look like -------------------------------------- */

  function isKitLine(item) {
    return !!(item && item.properties && item.properties[MARKER_KEY] === MARKER_VALUE);
  }

  /* Counts LINES, not units. */
  function countSubscriptionLines(cart) {
    var items = (cart && cart.items) || [];
    var count = 0;

    for (var i = 0; i < items.length; i++) {
      if (items[i].selling_plan_allocation) count++;
    }

    return count;
  }

  function buildProperties() {
    var properties = {};
    properties[MARKER_KEY] = MARKER_VALUE;
    return properties;
  }

  /* Returns the operations needed, empty when the cart is already correct. */
  function plan(cart) {
    var items = (cart && cart.items) || [];
    var products = kitProducts();
    var expected = countSubscriptionLines(cart);
    var operations = [];
    var seen = {};
    var i;

    for (i = 0; i < items.length; i++) {
      if (!isKitLine(items[i])) continue;

      var variantId = items[i].variant_id;
      var configured = products.some(function (product) {
        return String(product.variantId) === String(variantId);
      });

      /* Kits live on ONE line with quantity N. A duplicate line, or one for a
         product no longer configured, is cleared. */
      if (!configured || seen[variantId]) {
        operations.push({ id: items[i].key, quantity: 0 });
        continue;
      }

      seen[variantId] = items[i];
    }

    for (i = 0; i < products.length; i++) {
      var product = products[i];
      var wanted = expected * (product.quantity || 1);
      var line = seen[product.variantId];

      if (!line && wanted > 0) {
        operations.push({ add: { id: product.variantId, quantity: wanted, properties: buildProperties() } });
      } else if (line && line.quantity !== wanted) {
        /* By key, never by id: two lines can share a variant id. */
        operations.push({ id: line.key, quantity: wanted });
      }
    }

    return operations;
  }

  function reconcile() {
    if (!nativeFetch || stopped) return Promise.resolve();

    /* Disabled means hands off: nothing added, and carts already holding a kit
       are left exactly as they are. */
    if (!settings().enabled) return Promise.resolve();

    if (running) {
      queued = true;
      return Promise.resolve();
    }

    running = true;

    return readCart()
      .then(function (cart) {
        var operations = plan(cart);

        if (!operations.length) {
          debug('cart already correct, nothing to do');
          retried = false;
          return null;
        }

        if (overBudget()) return null;

        writeLog.push(Date.now());
        debug('applying', operations);
        snapshot = null;

        return apply(operations).then(function () {
          debug('syncing the cart app, without opening the drawer');
          refreshDrawer();
        });
      })
      .catch(function (error) {
        debug('reconcile failed', error && error.message);

        if (error && error.status === 422 && !retried) {
          retried = true;
          debug('cart was busy — retrying once in ' + RETRY_MS + 'ms');
          setTimeout(function () {
            running = false;
            reconcile();
          }, RETRY_MS);
        }
      })
      .then(function () {
        running = false;

        if (queued) {
          queued = false;
          schedule();
        }
      });
  }

  function schedule() {
    if (timer) clearTimeout(timer);
    timer = setTimeout(reconcile, QUIET_MS);
  }

  /* --- adding --------------------------------------------------------------
     Nothing here. An add reaches us the same way a removal does, through
     'cart:updated', and the kit follows a beat later.

     It used to go in front of the app's add, using its mu_custom_atc_logic
     hook, so the kit landed first. That bought nothing: the kit row is hidden,
     so no customer ever saw the difference, and it put our write on the
     critical path between a customer and buying something. The checkout guard
     below is what makes the cart right, so nothing else has to be quick.

     Detecting subscriptions from the cart rather than from the PDP form also
     covers adds the form never saw — drawer upsells, quick-add, anywhere. */

  /* --- checkout ------------------------------------------------------------
     mu_checkoutHook(url, setLoading) is awaited by the app immediately before
     it sends the customer to checkout:

       mu_checkoutHook && await mu_checkoutHook(url, re) || (location.href = url)

     A truthy return means "do not navigate, I am handling it". We return false,
     so the app navigates exactly as it always did — nothing here can redirect a
     customer. The button is already in its loading state, so the pause reads as
     a normal checkout.

     Why: our removal lands a beat after the customer's. Someone who deletes a
     subscription and checks out inside that window would otherwise carry an
     unearned kit into the order. It is the only moment where being briefly out
     of date costs anything, so it is the only moment anyone waits.

     Two rules, both absolute: never throw — a rejection here cancels the
     navigation and strands the customer — and never outlast the timeout. */

  /* One shot, straight to the cart API: no debounce, no cached item list, and
     the post-add hold ignored. Slower than a normal reconcile on purpose. */
  function verifyNow() {
    if (timer) clearTimeout(timer);

    return getCart().then(function (cart) {
      var operations = plan(cart);

      if (!operations.length) {
        debug('checkout: cart is already correct');
        return;
      }

      debug('checkout: correcting the cart first', operations);

      return apply(operations).then(refreshDrawer);
    });
  }

  var previousCheckoutHook = typeof window.mu_checkoutHook === 'function' ? window.mu_checkoutHook : null;

  window.mu_checkoutHook = function (url, setLoading) {
    return new Promise(function (resolve) {
      var settled = false;

      function done(handled) {
        if (settled) return;
        settled = true;
        resolve(handled || false);
      }

      setTimeout(function () {
        if (!settled) debug('checkout: ran out of time — letting it through');
        done(false);
      }, CHECKOUT_TIMEOUT_MS);

      try {
        if (!settings().enabled || !kitProducts().length) return done(false);

        debug('checkout: verifying the cart before letting it through');

        verifyNow().then(
          function () {
            if (!previousCheckoutHook) return done(false);

            return Promise.resolve(previousCheckoutHook(url, setLoading)).then(done, function () {
              done(false);
            });
          },
          function (error) {
            debug('checkout: verification failed, letting it through', error && error.message);
            done(false);
          }
        );
      } catch (e) {
        done(false);
      }
    });
  };

  /* The theme's own /cart page has none of the above: the app does not run
     there, so it publishes no events and its checkout hook never fires. The
     theme also changes quantities over AJAX without reloading, so even the
     page-load reconcile can be out of date by the time Checkout is clicked.
     Reachable from the checkout page's own cart link, so it has to be covered.

     Delegated from document rather than bound to the button, because the theme
     re-renders the cart footer after every change and would throw a bound
     listener away. Same two rules as the drawer guard: never block the
     customer, never outlast the timeout. */
  document.addEventListener(
    'click',
    function (event) {
      var button;

      try {
        button = event.target && event.target.closest ? event.target.closest('#checkout') : null;
      } catch (e) {
        return;
      }

      if (!button || button.getAttribute('data-welcome-kit-checked')) return;
      if (!settings().enabled || !kitProducts().length) return;

      event.preventDefault();
      debug('cart page: verifying before checkout');

      var released = false;

      function release() {
        if (released) return;
        released = true;

        try {
          /* Marked, clicked, unmarked: the click runs synchronously and the
             page leaves, and if it somehow does not, the guard rearms. */
          button.setAttribute('data-welcome-kit-checked', '1');
          button.click();
          setTimeout(function () {
            button.removeAttribute('data-welcome-kit-checked');
          }, 0);
        } catch (e) {
          /* no-op: the customer can always click again */
        }
      }

      setTimeout(release, CHECKOUT_TIMEOUT_MS);

      try {
        verifyNow().then(release, release);
      } catch (e) {
        release();
      }
    },
    true
  );

  /* --- following the cart --------------------------------------------------
     The app dispatches 'cart:updated' on document whenever its item list
     changes, and attaches the list. That covers removals, quantity changes and
     plan switches, and it fires on the optimistic update too — the instant the
     customer clicks the bin, not when the server answers.

     Its items are real Shopify line objects, so they can be used as they are. A
     list without keys is not one, and is ignored in favour of a read. */

  document.addEventListener('cart:updated', function (event) {
    try {
      var items = event && event.detail ? event.detail.items : null;

      var usable =
        Array.isArray(items) &&
        items.every(function (item) {
          return item && item.key;
        });

      if (usable) {
        snapshot = { items: items };
        snapshotAt = Date.now();
      }

      debug('the app published a new cart');
      schedule();
    } catch (e) {
      schedule();
    }
  });

  /* --- page load -----------------------------------------------------------
     Safety net for a customer who left before an add finished, and for a cart
     changed elsewhere. One read per page load. */

  function reconcileOnLoad() {
    debug('page load check');
    reconcile();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', reconcileOnLoad);
  } else {
    reconcileOnLoad();
  }

  /* For manual checks in the console. */
  window.BescherWelcomeKitReconcile = reconcile;
})();
